import React, {useMemo} from 'react';
import {useJobContext} from 'contexts/JobContext';
import {PartialJob} from 'shared/types/PartialJob';
import styled from 'styled-components';
import {formatPrice} from 'shared/helpers';
import {
    Table,
    Row,
    Item,
    TotalCost,
    CostFigure,
    TotalRow,
    ItemContainer,
} from 'components/customer/Confirmation/Checkout/Table';
import {Rooms} from 'components/customer/Confirmation/Checkout/Rooms';
import {useGetJobCostQuery} from 'components/customer/Job/store/jobApi';
import {Coupon} from 'shared/Coupon';
import {MinUsage} from 'components/customer/Confirmation/Checkout/MinUsage';
import {Freight} from 'components/customer/Confirmation/Checkout/Freight';
import {mobile} from 'shared/Breakpoints';
import {VariationCost} from 'components/customer/Confirmation/Checkout/VariationCost';
import {useAppContext} from 'contexts/ApplicationContext';
import {isDeviceSmall} from 'shared/helpers/DeviceSize';
import {MinimumChargeMoreInfo} from 'components/customer/Confirmation/Checkout/MinimumChargeMoreInfo';
import {useAppSelector} from 'store/customer';
import {
    selectAmountPaid,
    selectIsLoadingMisc,
    selectTotalRemovedVariationCost,
    selectTotalRemovedVariationCostGST,
} from 'components/customer/Confirmation/store/confirmationSlice';
import {useCheckProductVariations} from 'components/customer/Confirmation/helpers/useCheckProductVariations';
import {Alert, Spinner} from 'react-bootstrap';
import {Misc} from 'components/customer/Confirmation/Checkout/Misc';
import {MoreInfoStatic} from 'shared/components/MoreInfo/MoreInfo';
import {CostingMethod, PriceType} from 'shared/constants/PackagingOption';

const getDeliveryExtraPrice = (option: {
    has_price: boolean;
    needs_confirming: boolean;
    price_type: number;
    costing_method: number;
    cost: number;
}) => {
    if (!option.has_price) {
        return Number(option.costing_method) === CostingMethod.TO_BE_QUOTED
            ? 'To Be Quoted'
            : 'Price confirmed after review';
    }
    if (
        Number(option.price_type) === PriceType.INDICATIVE &&
        option.needs_confirming
    ) {
        return `${String(formatPrice(option.cost, {}))} (estimate)`;
    }
    return String(formatPrice(option.cost, {}));
};

export const Checkout = () => {
    const isSmallDevice = isDeviceSmall();
    const {userProfile} = useAppContext();
    const {job} = useJobContext() as PartialJob;
    const totalVariationCostReduction = useAppSelector(
        selectTotalRemovedVariationCost
    );
    const totalVariationGST = useAppSelector(
        selectTotalRemovedVariationCostGST
    );
    const amountPaid = useAppSelector(selectAmountPaid);
    const isLoadingMisc = useAppSelector(selectIsLoadingMisc);

    const {isLoading: isLoadingVariations} = useCheckProductVariations();
    const {data: jobCost, isLoading: isLoadingCost} = useGetJobCostQuery(
        {
            jobId: Number(job.id),
        },
        {
            skip: typeof job?.id === 'undefined',
        }
    );

    const total = useMemo(() => {
        return (
            (jobCost?.totalJobCost ?? 0) -
            totalVariationCostReduction -
            totalVariationGST -
            (amountPaid ?? 0)
        );
    }, [
        jobCost?.totalJobCost,
        totalVariationCostReduction,
        totalVariationGST,
        amountPaid,
    ]);
    const totalLabel =
        total <= 0
            ? 'Refund amount'
            : jobCost?.hasIndicativeDeliveryExtras
              ? 'Estimated Total:'
              : amountPaid > 0
                ? 'Total Balance:'
                : 'Total Cost:';

    return (
        <>
            <Table>
                <thead>
                    <tr>
                        <th>Item</th>
                        <th style={{width: isSmallDevice ? '100px' : '180px'}}>
                            Total
                        </th>
                        <th style={{width: isSmallDevice ? '70px' : '180px'}}>
                            Actions
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <Rooms />
                    {jobCost?.jobBenchtopsCost ? (
                        <Row>
                            <td>
                                <Item>Benchtops</Item>
                            </td>
                            <td>
                                {formatPrice(
                                    jobCost?.jobBenchtopsCost || 0,
                                    {}
                                )}
                            </td>
                            <td />
                        </Row>
                    ) : null}
                    {jobCost?.sundryCosts ? (
                        <Row>
                            <td>
                                <Item>Sundries</Item>
                            </td>
                            <td>
                                {formatPrice(jobCost?.sundryCosts || 0, {})}
                            </td>
                            <td />
                        </Row>
                    ) : null}

                    {userProfile?.isCouponAvailable && jobCost?.coupon ? (
                        <Row>
                            <td>
                                <Item>Coupon</Item>
                            </td>
                            <td>-{formatPrice(jobCost?.coupon || 0, {})}</td>
                            <td />
                        </Row>
                    ) : null}

                    <MinUsage jobCost={jobCost} />

                    {jobCost?.jobMinChargeFromCustMin &&
                    jobCost.jobMinChargeFromCustMin > 0 ? (
                        <Row>
                            <td>
                                <ItemContainer>
                                    <Item>Minimum charge</Item>
                                    <MinimumChargeMoreInfo />
                                </ItemContainer>
                            </td>
                            <td>
                                {formatPrice(
                                    jobCost?.jobMinChargeFromCustMin || 0,
                                    {}
                                )}
                            </td>
                            <td />
                        </Row>
                    ) : null}

                    <VariationCost jobCost={jobCost} />

                    <Freight jobCost={jobCost} />

                    {jobCost?.packagingOptions?.map((option) => (
                        <React.Fragment key={option.id}>
                            {option.show_category ? (
                                <Row>
                                    <td colSpan={3}>
                                        <Item>{option.display_category}</Item>
                                    </td>
                                </Row>
                            ) : null}
                            <Row>
                                <td>
                                    <ItemContainer>
                                        <Item>{option.name}</Item>
                                        <MoreInfoStatic
                                            hoverText="Delivery & Extras cost calculation"
                                            dialogClassName="modal-md"
                                            info={option.calculation}
                                        />
                                    </ItemContainer>
                                </td>
                                <td>{getDeliveryExtraPrice(option)}</td>
                                <td />
                            </Row>
                        </React.Fragment>
                    ))}

                    <Row>
                        <td>
                            <Item>{job?.countryTaxRateName || 'GST'}</Item>
                        </td>
                        <td>{formatPrice(jobCost?.tax || 0, {})}</td>
                        <td />
                    </Row>

                    <Misc />
                    {isLoadingCost || isLoadingVariations || isLoadingMisc ? (
                        <TotalRow>
                            <td colSpan={3}>
                                <LoaderContainer>
                                    <Spinner animation="border" />
                                </LoaderContainer>
                            </td>
                        </TotalRow>
                    ) : (
                        <TotalRow>
                            <td>
                                <TotalCost>{totalLabel}</TotalCost>
                            </td>
                            <td>
                                <CostFigure>
                                    {total > 0 ? (
                                        formatPrice(total, {})
                                    ) : (
                                        <RedTotal>
                                            -{formatPrice(Math.abs(total), {})}
                                        </RedTotal>
                                    )}
                                </CostFigure>
                            </td>
                            <td>
                                <CostFigure $em={false}>
                                    (Inc. {job?.countryTaxRateName || 'GST'})
                                </CostFigure>
                            </td>
                        </TotalRow>
                    )}
                    {userProfile?.isCouponAvailable ? (
                        <tr>
                            <td colSpan={3}>
                                <CouponContainer>
                                    <Coupon />
                                </CouponContainer>
                            </td>
                        </tr>
                    ) : null}
                </tbody>
            </Table>

            {total < 0 ? (
                <RefundAlert variant="info">
                    Looks like you were over charged for this job. We will
                    contact you shortly to process your refund of{' '}
                    <strong>{formatPrice(Math.abs(total), {})}</strong>.<br />
                    <br />
                    If you have any questions please contact us at{' '}
                    <a href={`tel:${userProfile?.manufacturerPhone}`}>
                        {userProfile?.manufacturerPhone}
                    </a>{' '}
                    or{' '}
                    <a href={`mailto:${userProfile?.manufacturerEmail}`}>
                        {userProfile?.manufacturerEmail}
                    </a>
                </RefundAlert>
            ) : null}
        </>
    );
};

const RefundAlert = styled(Alert)`
    margin-top: 15px;
`;

const RedTotal = styled.div`
    color: #f3a3a3;
`;

const LoaderContainer = styled.div`
    text-align: center;
    margin: 15px 0;
`;

const CouponContainer = styled.div`
    margin-top: 15px;
    padding-left: 51%;

    > div > div > input {
        height: 40px;
        font-size: 1em !important;
        color: ${({theme}) => theme.colors.primary?.main} !important;
    }
    > div > div > input::placeholder {
        color: ${({theme}) => theme.colors.primary?.main} !important;
    }

    > div > div > button {
        height: 40px;
        text-align: center;
        font-size: 1em !important;
        font-weight: 400;
    }

    ${mobile`
        padding-left: 0;
    `}
`;
